import { type RefSelectProps, Select } from 'antd'
import clsx from 'clsx'
import { useAtomValue } from 'jotai'
import { forwardRef, type Ref, useMemo } from 'react'

import { type SingleOrMultipleSelectProps } from '@/components/types'
import { DESCRIPTIONS } from '@/constants/common/message.constants'
import { useSitesByDcIdSelectQuery } from '@/features/sites/hooks'
import { type SiteEntity } from '@/libs/api'
import {
  useSelectExcludeNotExistValues,
  useSelectOnChangeHandler
} from '@/libs/hooks'
import { selectedDcIdAtom } from '@/routes/_protected/$tenantKey/-states'

import { mapToSiteFieldName } from './map-to-site-field-name'

type Props = SingleOrMultipleSelectProps<number, SiteEntity> & {
  tenantKey: string
  /** 拠点ID指定（指定しない場合は選択された拠点IDを使用して絞り込まれる） */
  dcId?: number
  /** 地点一覧指定（指定した場合は最優先で一覧として使用される） */
  sites?: SiteEntity[]
}

/**
 * 地点を選択するSelect
 */
export const SiteSelect = forwardRef(
  (
    {
      className,
      placeholder = DESCRIPTIONS.SELECT,
      tenantKey,
      dcId,
      value,
      mode,
      sites,
      onChange,
      ...props
    }: Props,
    ref: Ref<RefSelectProps> | undefined
  ) => {
    const selectedDcId = useAtomValue(selectedDcIdAtom)

    const { data, isFetching, isLoading } = useSitesByDcIdSelectQuery({
      tenantKey,
      dcId: dcId ?? selectedDcId
    })

    const handleChange = useSelectOnChangeHandler({ mode, onChange })
    const options = useMemo(() => {
      const target = sites ?? data
      return target?.map((x) => ({
        ...x,
        // nameで指定すると利用側で取得できるoption.nameの値が変わるため、labelで指定
        label: mapToSiteFieldName(x)
      }))
    }, [data, sites])

    const excludeNotExistValues = useSelectExcludeNotExistValues(
      sites ?? data,
      handleChange
    )

    return (
      <Select
        ref={ref}
        {...props}
        mode={mode}
        value={isLoading ? null : excludeNotExistValues(value)}
        onChange={handleChange}
        optionFilterProp="label"
        fieldNames={{ label: 'label', value: 'id' }}
        className={clsx(['min-w-[280px]', className])}
        options={options?.filter((site) => site.isArea === false)}
        loading={isFetching}
        placeholder={placeholder}
      />
    )
  }
)
